import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Consolidated home page data hook.
 * Uses a single edge function call to fetch ALL home data at once,
 * reducing network waterfall from 7+ requests to 1 request.
 */

export interface QuizLearnSetBasic {
  id: string;
  title: string;
  description: string | null;
  icon: string | null;
  is_published: boolean;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface LearningPath {
  id: string;
  title: string;
  description: string | null;
  icon: string | null;
  cover_image_url: string | null;
  is_published: boolean;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface LearningPathItem {
  id: string;
  learning_path_id: string;
  item_type: string;
  item_id: string;
  order_index: number;
  created_at: string;
}

export interface HomeData {
  // Usage counts kept for backwards compatibility with HomeFeaturedTopics
  usageCounts?: { course_id: string; usage_count: number }[];
  courses: {
    id: string;
    title: string;
    description: string | null;
    cover_image_url: string | null;
    is_published: boolean;
    order_index: number;
    tags: string[];
    category: string | null;
    sort_priority: number | null;
    created_at: string;
    updated_at: string;
  }[];
  quizLearnSets: QuizLearnSetBasic[];
  learningPaths: LearningPath[];
  learningPathItems: LearningPathItem[];
  endlessQuizQuestions: {
    id: string;
    question: string;
    options: string[];
    correct_index: number;
    difficulty: "easy" | "medium" | "hard";
    explanation: string | null;
    tags: string[] | null;
    option_groups?: string[];
  }[];
  rotatingWords: string[];
  popularTags: string[] | null;
  textAnimationStyle: string;
}

const DEFAULT_ROTATING_WORDS = ["AI Agents", "Feedback", "RAG", "Focus", "Prompts", "Habits"];

export function useHomeData() {
  return useQuery({
    queryKey: ["home-data-aggregated", "v3"],
    queryFn: async (): Promise<HomeData> => {
      // Single edge function call replaces 7+ individual queries
      const { data, error } = await supabase.functions.invoke("home-data");
      
      if (error) {
        console.error("home-data edge function error:", error);
        throw error;
      }

      // Use defaults if rotating words is empty
      const rotatingWords = data?.rotatingWords?.length > 0 
        ? data.rotatingWords 
        : DEFAULT_ROTATING_WORDS;

      return {
        courses: data?.courses ?? [],
        quizLearnSets: data?.quizLearnSets ?? [],
        learningPaths: data?.learningPaths ?? [],
        learningPathItems: data?.learningPathItems ?? [],
        endlessQuizQuestions: data?.endlessQuizQuestions ?? [],
        rotatingWords,
        popularTags: data?.popularTags?.length > 0 ? data.popularTags : null,
        textAnimationStyle: data?.textAnimationStyle ?? "rolling",
      };
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    refetchOnWindowFocus: false,
  });
}
